<template lang="pug">
.author-home.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Home – all courses

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/editor/${firmCode}_${courseId}` : `#/editor/${courseId}`"
        )
          | Edit #[b {{ baseYamlFilename }}]
          
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/history/${firmCode}_${courseId}` : `#/history/${courseId}`"
        )
          | View #[b {{ baseYamlFilename }}] history
    .ms-auto
    ToggleDark

  .flex-1.p-24.overflow-y-auto.overflow-x-visible
    .max-w-screen-2xl.mx-auto
      .prose.prose.dark_prose-invert.max-w-none
          h1.font-normal 
           span Add <b>{{ baseYamlFilename }}</b> to Portal 

          p.opacity-80 
            span(v-if="title")                
              a(:href="`#/editor/${baseYamlFilename.slice(0,-4)}`") {{ title }} 
              |  
              | &ndash;
            span(v-if="lastUpdated") 
              | Last updated 
              |  
              a(:href="`#/history/${baseYamlFilename.slice(0,-4)}`") {{ formatTimeAgo(lastUpdated) }}

          template(v-if="loadCourseError")
            p.text-xl.italic 🚨 {{ loadCourseError }}
          
          template(v-else-if="!lastUpdated")
            p.animate-loading Loading...
          
          template(v-else-if="isAlreadyAddedToPortal")
            p.italic 🚨 This course was added to the client's portal on {{ courseDetails.alreadyAddedDate.toString() }}. 🚨 
            p.ital It cannot be added again. 
            p.italic In the near future, you will be able to update the course's metadata here.
          
          div(v-else="")       
            fieldset.p-4.my-6.bg-gray-100.dark_bg-gray-800.bg-opacity-10.rounded-lg.prose.prose.dark_prose-invert
              h2 Add course details
              
              template(v-if="!lastUpdated")
                p.text-xl.italic 🚨 No course found.

              form(@submit.prevent="confirmAndPublish")
                .mb-4
                  label(for="title") Title
                    #title
                      input(
                        type="text"
                        v-model="courseDetails.title"
                        required)

                .mb-4
                  label(for="suiteId") Suite
                  #suiteId
                    select(v-model="courseDetails.suiteId", required)
                      option(v-for="(item, index) in suitesAndTopics.filter((s) => s.internalParentId === 0)" :key="index" :value="item.internalId") {{ item.title }}
                      option(value="0") Other

                .mb-4(v-if='courseDetails.suiteId === 1')
                  label(for="topicId") Topic
                  #topicId
                    select(v-model="courseDetails.topicId", required)
                      option(v-for="(item, index) in suitesAndTopics.filter((s) => s.internalParentId === 1)" :key="index" :value="item.internalId") {{ item.title }}
                      option(value="0") Other

                .mb-4(v-if='courseDetails.suiteId === 2')
                  label(for="topicId") Topic
                  #topicId
                    select(v-model="courseDetails.topicId", required)
                      option(v-for="(item, index) in suitesAndTopics.filter((s) => s.internalParentId === 2)" :key="index" :value="item.internalId") {{ item.title }}
                      option(value="0") Other

                .mb-4(v-if='courseDetails.suiteId === 3') 
                  label(for="topicId") Topic
                  #topicId
                    select(v-model="courseDetails.topicId", required)
                      option(v-for="(item, index) in suitesAndTopics.filter((s) => s.internalParentId === 3)" :key="index" :value="item.internalId") {{ item.title }}
                      option(value="0") Other


                .mb-4(v-if='courseDetails.suiteId === 4')
                  label(for="topicId") Topic
                  #topicId
                    select(v-model="courseDetails.topicId", required)
                      option(v-for="(item, index) in suitesAndTopics.filter((s) => s.internalParentId === 4)" :key="index" :value="item.internalId") {{ item.title }}
                      option(value="0") Other

                .mb-4(v-if='courseDetails.suiteId === 5')
                  label(for="topicId") Topic
                  #topicId
                    select(v-model="courseDetails.topicId", required)
                      option(v-for="(item, index) in suitesAndTopics.filter((s) => s.internalParentId === 5)" :key="index" :value="item.internalId") {{ item.title }}
                      option(value="0") Other              

                .mb-4
                  label(for="duration") Duration
                  #duration
                    input(
                      type="text"
                      v-model="courseDetails.duration"
                      placeholder="HH:MM"
                      pattern="[0-9]{2}:[0-9]{2}"
                      title="Please enter the duration in HH:MM format."
                      required)

                .mb-4
                  label(for="completionCriteria") Is it scored?
                  #completionCriteria
                    select(v-model="courseDetails.completionCriteria", required)
                      option(value="2") Yes
                      option(value="1") No

                .mb-4(v-if='courseDetails.completionCriteria === "2"')
                  label(for="completionCriteria") What is the passmark?
                  #passmark
                    select(v-model="courseDetails.passmark", required)
                      option(v-for="number in numbersDivisibleBy5To100" :key="number" :value="number") {{ number }}

                .mb-4
                  label(for="description") Course Description
                  #description
                    textarea.w-full(v-model="courseDetails.description")

                .mb-4
                  label(for="objectives") Learning Objectives
                  #objectives  
                    textarea.w-full(v-model="courseDetails.objectives")
                      

                button.ms-auto.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded.mt-8(
                  :disabled="statuses.addingCourse !== 'idle'"
                ) Send to Portal
                span.px-4
                  span.italic.text-red-700.dark_text-red-400(v-if="statuses.addingCourse === 'error'") Something went wrong.
                  span.animate-loading.italic.opacity-75(v-else-if="statuses.addingCourse === 'pending'") Working...
                  span.italic.opacity-75.text-green-300(v-else-if="statuses.addingCourse === 'success'") Success!
</template>
<script setup lang="ts">
// The bespoke course needs to be added to the licenced courses

// It needs to get sync'd with the licenced courses table - is that automatic? Or do we need to initiate that manually?

// Testing how the course behaves with Portal UI in terms of Suites and Topics

import MainMenu from './MainMenu.vue';
import { formatTimeAgo } from '@vueuse/core';
import ToggleDark from './ToggleDark.vue';
import { onMounted, ref, computed, reactive } from 'vue';
import { loadCourse } from '../store/courses';
import { publishBespoke } from '../lib/callModContentAuthors';
import { getClientBespokeCourses } from '../lib/callModContentAuthors';
import { getSuitesAndTopics } from '../lib/callModContentAuthors';

const props = defineProps<{ courseId: number; firmCode?: string }>();
const lastUpdated = ref<Date>();
const isAlreadyAddedToPortal = ref(false);
const course = ref();
const title = ref<string>();
const loadCourseError = ref('');
const baseYamlFilename = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.yml`
    : `${props.courseId}.yml`
);
const suitesAndTopics = ref([]);
const numbersDivisibleBy5To100 = Array.from(
  { length: 21 },
  (_, i) => i * 5
).reverse();
const statuses = reactive<{
  [name: string]: 'idle' | 'pending' | 'success' | 'error';
}>({
  addingCourse: 'idle'
});

let courseDetails = reactive({
  firmCode: props.firmCode || '',
  vinciResourceId: props.courseId,
  title: title.value || '',
  suiteId: '0',
  topicId: '0',
  suiteTitle: '',
  topicTitle: '',
  duration: '',
  passmark: '0',
  resourceAssetPath:
    'https://lms.vinciworks.com/awscourses/custom/' + props.firmCode,
  vendorResourceCode: 'BSPK-' + props.courseId,
  thumbnail: `https://vinciworks-client-portal-assets.s3.eu-west-2.amazonaws.com/${props.firmCode}_${props.courseId}.jpg`,
  completionCriteria: '1',
  alreadyAddedDate: '',
  isActive: true,
  description:
    '<p>This bespoke e-learning course has been designed to address specific learning needs, delivering targeted knowledge and skills in a tailored format.</p>',
  objectives:
    '<p>The objectives of this course have been crafted to support your learning needs according to the distinct requirements of your organisation.</p>'
});

const load = async () => {
  try {
    course.value = await loadCourse(props);
    if (!course.value) throw new Error('Course not found.');
    let { latest, latestDraft, hasUnsavedChanges } = course.value;
    if (!latest?.courseId) throw new Error('Course not found.');

    lastUpdated.value = new Date(+latest.metadata.updatedDate);
    title.value = latest.title;

    if (hasUnsavedChanges) {
      const draftAuthor = latestDraft?.author;
      throw new Error(`Course has unsaved changes from ${draftAuthor}.`);
    }

    courseDetails.title = title.value || '';

    suitesAndTopics.value = await getSuitesAndTopics();

    const clientBespokeCoursesResponse = await getClientBespokeCourses(
      props.firmCode,
      [props.courseId]
    );

    if (!clientBespokeCoursesResponse)
      throw new Error(
        'There was an error while checking if this course already exists on Portal.'
      );

    isAlreadyAddedToPortal.value =
      clientBespokeCoursesResponse.data.data.totalResults > 0;
    if (isAlreadyAddedToPortal.value) {
      courseDetails.alreadyAddedDate = new Date(
        +clientBespokeCoursesResponse.data.data.results[0].metadata.createdDate
      );
    }
  } catch (e) {
    loadCourseError.value = e.message;
  }
};

onMounted(load);

const publishBespokeData = async () => {
  statuses.addingCourse = 'pending';

  const data = {
    title: courseDetails.title,
    suiteId: parseInt(courseDetails.suiteId),
    topicId: parseInt(courseDetails.topicId),
    //- Double Bang to prevent look up of suite/topic with id of 0/Other which doesn't exist
    suiteTitle: !!parseInt(courseDetails.suiteId)
      ? suitesAndTopics.value.find(s => s.internalId === courseDetails.suiteId)
          .title
      : '',
    topicTitle: !!parseInt(courseDetails.topicId)
      ? suitesAndTopics.value.find(s => s.internalId === courseDetails.topicId)
          .title
      : '',
    duration: courseDetails.duration,
    passMark: parseInt(courseDetails.passmark),
    vinciResourceId: courseDetails.vinciResourceId,
    resourceAssetPath: courseDetails.resourceAssetPath,
    tenantId: courseDetails.firmCode,
    firmCode: courseDetails.firmCode,
    vendorResourceCode: courseDetails.vendorResourceCode,
    additionalInfo: {
      thumbnail: courseDetails.thumbnail,
      CourseDescription: courseDetails.description
    },
    resourceMaster: {
      CompletionCriteria: parseInt(courseDetails.completionCriteria)
    },
    description: courseDetails.description,
    objectives: courseDetails.objectives,
    howTaken: courseDetails.objectives
  };

  await publishBespoke(data)
    .then(() => {
      statuses.addingCourse = 'success';
    })
    .catch(e => {
      statuses.addingCourse = 'error';
    });
};

const confirmAndPublish = () => {
  if (
    confirm(
      'Please double check the course details you have entered.\n\nYou do not yet have the opportunity to update or correct the information once it has been sent.\n\nAre you sure you want to send this to the Portal?'
    )
  ) {
    publishBespokeData();
  }
};
</script>

<style scoped>
.dark select option {
  background: #333;
  color: #fff;
}
</style>
